import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import useTranslations from '@/i18n/useTranslations';
import { formatArtworkTitleForDisplay } from '@/pages/artwork/utils/artworkTitleDisplay.utils';
import { getArtworkHoldingType } from '@/pages/artwork/utils/artworkView.selectors';
import ViewInRoomEye from '@/shared-components/icons/components/artwork/ViewInRoomEye';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import { buildAndroidRoomIntent, getOriginalRoomViewSource, getRoomViewPlatform } from './roomView.utils';

import './RoomViewAction.css';

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export default function RoomViewAction({ artworkData, holdingTypeOverride = null }) {
  const t = useTranslations('common.shared.artwork_view.room_view');
  const [dialog, setDialog] = useState(null);
  const source = getOriginalRoomViewSource(artworkData);
  const platform = getRoomViewPlatform();
  const slug = artworkData?.slug;
  const iosQuickLookSupported = platform !== 'ios'
    || (typeof document !== 'undefined' && document.createElement('a').relList?.supports?.('ar'));

  useEffect(() => {
    if (typeof window === 'undefined' || !slug) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('room_ar_unavailable') !== slug) return;
    setDialog('unavailable');
    url.searchParams.delete('room_ar_unavailable');
    window.history.replaceState(window.history.state, '', url.toString());
  }, [slug]);

  useEffect(() => {
    if (!dialog) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setDialog(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [dialog]);

  const isLimitedEditionHolding = (holdingTypeOverride ?? getArtworkHoldingType(artworkData)) === 'limited_edition';

  const artworkUrl = typeof window === 'undefined'
    ? ''
    : `${window.location.origin}/artwork/${encodeURIComponent(slug)}`;
  const fallbackUrl = `${artworkUrl}?room_ar_unavailable=${encodeURIComponent(slug)}`;
  const title = formatArtworkTitleForDisplay(artworkData?.artwork_about?.artwork_title) || 'Artwork';
  const label = t('action');
  const icon = <ViewInRoomEye className="artwork-room-view__icon" />;
  const closeDialog = (event) => {
    event?.stopPropagation();
    setDialog(null);
  };

  let control;
  if (!source || isLimitedEditionHolding) {
    const explanation = t(isLimitedEditionHolding ? 'edition_unavailable' : 'artwork_unavailable');
    control = (
      <button
        type="button"
        className="artwork-room-view__action artwork-room-view__action--unavailable"
        aria-label={`${label}. ${explanation}`}
        title={explanation}
        onClick={(event) => {
          event.stopPropagation();
          setDialog(isLimitedEditionHolding ? 'edition_unavailable' : 'artwork_unavailable');
        }}
      >
        {icon}
      </button>
    );
  } else if (platform === 'ios' && iosQuickLookSupported) {
    control = (
      <a
        href={`${source.usdzUrl}#allowsContentScaling=0`}
        rel="ar"
        className="artwork-room-view__action"
        aria-label={label}
        title={label}
        onClick={(event) => event.stopPropagation()}
      >
        <img className="artwork-room-view__quick-look-image" src={TRANSPARENT_PIXEL} alt="" />
        {icon}
      </a>
    );
  } else if (platform === 'android') {
    control = (
      <a
        href={buildAndroidRoomIntent(source, fallbackUrl, title)}
        className="artwork-room-view__action"
        aria-label={label}
        title={label}
        onClick={(event) => event.stopPropagation()}
      >
        {icon}
      </a>
    );
  } else {
    control = (
      <button
        type="button"
        className="artwork-room-view__action"
        aria-label={label}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          setDialog(platform === 'ios' ? 'unavailable' : 'handoff');
        }}
      >
        {icon}
      </button>
    );
  }

  return (
    <>
      <div className="artwork-label-socials__item artwork-room-view__item">
        {control}
        <span className="artwork-label-socials__item-count artwork-label-socials__item-count--empty" aria-hidden="true">{' '}</span>
      </div>
      {dialog && (
        <GenericPopup
          open={Boolean(dialog)}
          className="generic-popup_content remove-scrollbar"
          opacity
          onBackgroundClick={closeDialog}
        >
          <div className="generic-popup__header">
            <h2 className="generic-popup__header-text">{t('title')}</h2>
            <XIcon
              className="generic-popup__close"
              onClick={closeDialog}
              role="button"
              tabIndex={0}
              aria-label={t('close')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  closeDialog(event);
                }
              }}
            />
          </div>
          <div className="artwork-room-view__content">
            {dialog === 'handoff' ? (
              <>
                <p>{t('desktop_instruction')}</p>
                <div className="artwork-room-view__qr"><QRCode value={artworkUrl} size={180} /></div>
                <p className="artwork-room-view__size">{source.width} × {source.height} {source.unit}</p>
              </>
            ) : <p>{t(dialog === 'unavailable' ? 'unavailable' : dialog)}</p>}
          </div>
        </GenericPopup>
      )}
    </>
  );
}
